import { useState, useEffect } from 'react';
import { propertyAdminService, assemblyService } from 'services/api';
import PropertyAdminModal from 'components/PropertyAdminModal';
import PropertyAdminEditModal from './PropertyAdminEditModal';
import AssemblyAdminModal, { assemblyAdminService } from './AssemblyAdminModal';
import TempCredentialsModal from './TempCredentialsModal';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import Paginacion from 'components/Paginacion';
import { ESTADO_ASAMBLEA as ASM_STATUS_LABEL } from 'utils/etiquetas';
import styles from './PropertyAdmins.module.css';

const POR_PAGINA = 20;
const STATUS_COLOR = { active: '#12b76a', trial: '#f79009', inactive: '#f04438' };
const STATUS_LABEL = { active: 'Activa', trial: 'Trial',   inactive: 'Inactiva' };
const ASM_STATUS_COLOR = { pendiente: '#f79009', en_progreso: '#12b76a', finalizada: '#8a92a6' };

// ── Componente principal ───────────────────────────────────────────────────────
export default function PropertyAdmins() {
  const [data,       setData]       = useState({ property_admins: [], assembly_admins: [] });
  const [loading,    setLoading]    = useState(true);
  const [alert,      setAlert]      = useState(null);
  const [tab,        setTab]        = useState('property'); // 'property' | 'assembly'
  const [assemblies, setAssemblies] = useState([]);
  const [tempCredentials, setTempCredentials] = useState(null); // { email, password }

  // Modals
  const [modalPropAdmin, setModalPropAdmin]   = useState(null); // PropertyAdminModal — solo 'new' acá (crear/asignar)
  const [modalEditAdmin, setModalEditAdmin]   = useState(null); // PropertyAdminEditModal — editar admin existente
  const [modalAsmAdmin,  setModalAsmAdmin]    = useState(null); // null | 'new' | { editing }
  const [confirmDel,     setConfirmDel]       = useState(null);
  const [deleting,       setDeleting]         = useState(false);
  const [delError,       setDelError]         = useState('');
  const [pagProp, setPagProp] = useState(1);   // página de «Admins de propiedad»
  const [pagAsm,  setPagAsm]  = useState(1);   // página de «Admins de asamblea»

  const load = async () => {
    setLoading(true);
    try {
      const [adminsRes, asmRes] = await Promise.all([
        propertyAdminService.getAll(),
        assemblyService.getAll({ limit: 200 }),
      ]);
      setData(adminsRes.data || { property_admins: [], assembly_admins: [] });
      setAssemblies(asmRes.data.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSaved = (msg, credentials = null) => {
    setAlert({ type: 'success', message: msg });
    setModalPropAdmin(null);
    setModalEditAdmin(null);
    setModalAsmAdmin(null);
    if (credentials) setTempCredentials(credentials);
    load();
  };

  const del = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    setDelError('');
    try {
      if (confirmDel.type === 'property') await propertyAdminService.delete(confirmDel.id);
      else                                await assemblyAdminService.delete(confirmDel.id);
      setAlert({ type: 'success', message: 'Eliminado correctamente' });
      setConfirmDel(null);
      load();
    } catch (err) {
      setDelError(err.response?.data?.message || 'No se pudo eliminar. Inténtalo de nuevo.');
    } finally { setDeleting(false); }
  };

  const propAdmins = data.property_admins || [];
  const asmAdmins  = data.assembly_admins  || [];
  // Se pagina en el navegador (lista de super_admin, cientos de filas). Si al borrar o editar la
  // página actual queda fuera de rango, se muestra la última.
  const paginaDe = (pag, n) => Math.min(pag, Math.max(1, Math.ceil(n / POR_PAGINA)));
  const paginaProp = paginaDe(pagProp, propAdmins.length);
  const paginaAsm  = paginaDe(pagAsm,  asmAdmins.length);
  const propVisibles = propAdmins.slice((paginaProp - 1) * POR_PAGINA, paginaProp * POR_PAGINA);
  const asmVisibles  = asmAdmins.slice((paginaAsm - 1) * POR_PAGINA,  paginaAsm * POR_PAGINA);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-user-shield ${styles.iconoCabecera}`} />Administradores</h1>
          <p>Gestión de administradores de propiedad y de asamblea</p>
        </div>
        <button
          className={`btn btn-primary ${tab === 'assembly' ? styles.botonMorado : ''}`}
          onClick={() => tab === 'property' ? setModalPropAdmin('new') : setModalAsmAdmin('new')}>
          <i className="fas fa-plus" />
          {tab === 'property' ? 'Nuevo admin de propiedad' : 'Nuevo admin de asamblea'}
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.aviso}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      {/* Tabs */}
      <div className={styles.pestanas}>
        {[
          { key: 'property', label: `Admins de propiedad (${propAdmins.length})`, icon: 'fa-building', color: 'var(--accent)' },
          { key: 'assembly', label: `Admins de asamblea (${asmAdmins.length})`,   icon: 'fa-gavel',    color: '#6c63ff' },
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setPagProp(1); setPagAsm(1); }}
            className={tab === t.key ? styles.pestanaActiva : styles.pestana}
            style={{ '--tono': t.color }}>
            <i className={`fas ${t.icon} ${styles.pestanaIcono}`} />
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="loading"><div className="spinner" /></div> : (

        /* ── Tab: Admins de propiedad ── */
        tab === 'property' ? (
          propAdmins.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <i className="fas fa-user-shield" />
                <p>No hay administradores de propiedad registrados</p>
                <button className={`btn btn-primary ${styles.margenCrearPrimero}`} onClick={() => setModalPropAdmin('new')}>
                  <i className="fas fa-plus" /> Crear primero
                </button>
              </div>
            </div>
          ) : (
            <>
            <div className="card">
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Administrador</th><th>Contacto</th><th>Propiedad</th><th>Membresía</th><th className={styles.colCentro}>Acciones</th></tr>
                  </thead>
                  <tbody>
                    {propVisibles.map(pa => {
                      const sub = pa.property?.subscription_status;
                      return (
                        <tr key={pa.id}>
                          <td>
                            <div className={styles.nombre}>{pa.user?.name} {pa.user?.last_name}</div>
                            <div className={styles.correo}>{pa.user?.email}</div>
                          </td>
                          <td className={styles.contacto}>{pa.user?.phone || '—'}</td>
                          <td>
                            {pa.property ? (
                              <div>
                                <div className={styles.nombre}>{pa.property.name}</div>
                              </div>
                            ) : <span className={styles.sinDato}>Sin propiedad</span>}
                          </td>
                          <td>
                            {sub ? (
                              <span className={styles.chip}
                                style={{ '--tono': STATUS_COLOR[sub] || '#888', '--fondo': (STATUS_COLOR[sub] || '#888') + '22' }}>
                                {STATUS_LABEL[sub] || sub}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            <div className={`actions ${styles.accionesCentro}`}>
                              <button className="btn btn-sm btn-primary btn-icon"
                                onClick={() => setModalEditAdmin(pa)} title="Editar" aria-label="Editar">
                                <i className="fas fa-pencil" />
                              </button>
                              <button className="btn btn-sm btn-danger btn-icon"
                                onClick={() => setConfirmDel({ id: pa.id, type: 'property', name: `${pa.user?.name} ${pa.user?.last_name}` })}
                                title="Desvincular" aria-label="Desvincular">
                                <i className="fas fa-link-slash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <Paginacion pagina={paginaProp} total={propAdmins.length} porPagina={POR_PAGINA} onCambiar={setPagProp} />
            </>
          )
        ) : (

          /* ── Tab: Admins de asamblea ── */
          asmAdmins.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <i className={`fas fa-gavel ${styles.vacioIconoMorado}`} />
                <p>No hay administradores de asamblea registrados</p>
                <button className={`btn btn-primary ${styles.margenCrearPrimero} ${styles.botonMorado}`}
                  onClick={() => setModalAsmAdmin('new')}>
                  <i className="fas fa-plus" /> Crear primero
                </button>
              </div>
            </div>
          ) : (
            <>
            <div className="card">
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Administrador</th><th>Contacto</th><th>Asamblea asignada</th><th>Estado asamblea</th><th className={styles.colCentro}>Acciones</th></tr>
                  </thead>
                  <tbody>
                    {asmVisibles.map(am => {
                      const asmStatus = am.assembly?.status;
                      return (
                        <tr key={am.id}>
                          <td>
                            <div className={styles.fichaAdmin}>
                              <div className={styles.avatar}>
                                <i className={`fas fa-user-tie ${styles.avatarGlifo}`} />
                              </div>
                              <div>
                                <div className={styles.nombre}>{am.user?.name} {am.user?.last_name}</div>
                                <div className={styles.correo}>{am.user?.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className={styles.contacto}>{am.user?.phone || '—'}</td>
                          <td>
                            {am.assembly ? (
                              <div>
                                <div className={styles.nombre}>
                                  Asamblea #{am.assembly.number_assembly}
                                </div>
                                <div className={styles.correo}>
                                  {am.assembly.property?.name || `Propiedad #${am.assembly.property_id}`}
                                </div>
                              </div>
                            ) : (
                              <span className={styles.sinAsamblea}>
                                Sin asamblea asignada
                              </span>
                            )}
                          </td>
                          <td>
                            {asmStatus ? (
                              <span className={styles.chip}
                                style={{ '--tono': ASM_STATUS_COLOR[asmStatus] || '#888', '--fondo': (ASM_STATUS_COLOR[asmStatus] || '#888') + '22' }}>
                                {ASM_STATUS_LABEL[asmStatus] || asmStatus}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            <div className={`actions ${styles.accionesCentro}`}>
                              <button className="btn btn-sm btn-secondary btn-icon"
                                onClick={() => setModalAsmAdmin({ editing: am })} title="Editar" aria-label="Editar">
                                <i className="fas fa-pencil" />
                              </button>
                              <button className="btn btn-sm btn-danger btn-icon"
                                onClick={() => setConfirmDel({ id: am.id, type: 'assembly', name: `${am.user?.name} ${am.user?.last_name}` })}
                                title="Eliminar" aria-label="Eliminar">
                                <i className="fas fa-link-slash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <Paginacion pagina={paginaAsm} total={asmAdmins.length} porPagina={POR_PAGINA} onCambiar={setPagAsm} />
            </>
          )
        )
      )}

      {/* Modal crear/asignar admin de propiedad — modalPropAdmin solo es 'new' acá
          (nunca la fila de un admin existente, ver PropertyAdminEditModal para editar) */}
      {modalPropAdmin && (
        <PropertyAdminModal
          onClose={() => setModalPropAdmin(null)}
          onSuccess={onSaved}
        />
      )}

      {/* Modal editar admin de propiedad existente */}
      {modalEditAdmin && (
        <PropertyAdminEditModal
          propertyAdmin={modalEditAdmin}
          onClose={() => setModalEditAdmin(null)}
          onSaved={onSaved}
        />
      )}

      {/* Modal admin de asamblea */}
      {modalAsmAdmin && (
        <AssemblyAdminModal
          editing={modalAsmAdmin !== 'new' ? modalAsmAdmin.editing : null}
          assemblies={assemblies}
          onClose={() => setModalAsmAdmin(null)}
          onSaved={onSaved}
        />
      )}

      {/* Modal credenciales temporales */}
      {tempCredentials && (
        <TempCredentialsModal credentials={tempCredentials} onClose={() => setTempCredentials(null)} />
      )}

      {/* Modal confirmación eliminar */}
      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar administrador" onClose={() => { setConfirmDel(null); setDelError(''); }} onConfirm={del} deleting={deleting} error={delError}>
          <div className={styles.confirmaTitulo}>
            ¿Eliminar a <span className={styles.confirmaNombre}>{confirmDel.name}</span>?
          </div>
          <div className={styles.confirmaDetalle}>
            {confirmDel.type === 'assembly'
              ? 'Se desvinculará al administrador de asamblea. El usuario permanece en el sistema.'
              : 'Se desvinculará al administrador de la propiedad. El usuario permanece en el sistema.'}
          </div>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}